
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Zap, Globe, Bot, CreditCard, Rocket, Lock, Layers, Cpu, Repeat, Smartphone, Terminal, Wallet, CheckCircle2 } from 'lucide-react';

const RoadmapCard = ({ quarter, title, status, items, icon: Icon, delay }: { 
    quarter: string; 
    title: string; 
    status: 'completed' | 'current' | 'upcoming'; 
    items: string[]; 
    icon: any;
    delay: number;
}) => (
    <motion.div 
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay }}
        className="relative pl-12 pb-16 last:pb-0"
    >
        <div className="absolute left-[19px] top-0 bottom-0 w-px bg-stone-200 dark:bg-white/10 group-last:bottom-auto"></div>
        
        <div className={`absolute left-0 top-0 w-10 h-10 rounded-xl flex items-center justify-center border-2 z-10 transition-all duration-500 ${
            status === 'completed' ? 'bg-emerald-500 border-emerald-500 text-white shadow-lg' :
            status === 'current' ? 'bg-nobel-gold border-nobel-gold text-black shadow-xl scale-110' :
            'bg-white dark:bg-[#0A0B0F] border-stone-200 dark:border-white/10 text-stone-400'
        }`}>
            <Icon size={20} strokeWidth={status === 'current' ? 2.5 : 2} />
        </div>

        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                    <span className={`text-[10px] font-bold uppercase tracking-[0.3em] px-2 py-0.5 rounded ${
                        status === 'current' ? 'bg-nobel-gold/20 text-nobel-gold' : 'text-stone-400'
                    }`}>
                        {quarter}
                    </span>
                    <h3 className="text-2xl font-serif font-bold text-stone-900 dark:text-white mt-1 tracking-tight">{title}</h3>
                </div>
                {status === 'current' && (
                    <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-500 bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20 shadow-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                        Active Epoch
                    </div>
                )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
                {items.map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 rounded-2xl bg-white dark:bg-white/[0.02] border border-stone-100 dark:border-white/5 hover:border-nobel-gold/30 transition-all group">
                        <div className="mt-1 w-1.5 h-1.5 rounded-full bg-stone-300 dark:bg-white/20 group-hover:bg-nobel-gold transition-colors shrink-0" />
                        <span className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed group-hover:text-stone-900 dark:group-hover:text-stone-200 transition-colors">
                            {item}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    </motion.div>
);

export const RoadmapDoc = () => (
    <div className="space-y-16 animate-fade-in">
        <section className="max-w-3xl">
            <h3 className="text-3xl font-serif font-bold text-stone-900 dark:text-white mb-6 tracking-tight">Sovereign Expansion Protocol</h3>
            <p className="text-lg text-stone-600 dark:text-stone-300 leading-relaxed font-light">
                The current roadmap defines our trajectory toward decentralized sovereignty. From the stabilization of core settlement rails to the launch of aerospace-grade physical hardware.
            </p>
        </section>

        <div className="relative">
            <RoadmapCard 
                quarter="Phase 1"
                title="Infrastructure Stabilization"
                status="current"
                icon={Rocket}
                delay={0.1}
                items={[
                    "GHOST Native Token TGE on Sui Mainnet",
                    "GhostOS Secure Runtime stabilization",
                    "Unified Multi-Chain Dashboard rollout",
                    "Beta Merchant Onboarding"
                ]}
            />
            <RoadmapCard 
                quarter="Phase 2"
                title="Retail & Mobility Launch"
                status="upcoming"
                icon={Smartphone}
                delay={0.2}
                items={[
                    "GhostWallet Mobile launch (iOS/Android)",
                    "wGHOST Solana Bridge & Jupiter integration",
                    "NFC 'Tap-to-Pay' support for mobile merchants",
                    "Initial Ghost Card Virtual Issuance"
                ]}
            />
            <RoadmapCard 
                quarter="Phase 3"
                title="Intelligence & Automation"
                status="upcoming"
                icon={Bot}
                delay={0.3}
                items={[
                    "x402 Micropayment Agent SDK v1.0",
                    "Autonomous AI Treasury Balancing",
                    "Privacy-preserving Escrow expansion",
                    "Institutional White-Label Portal launch"
                ]}
            />
            <RoadmapCard 
                quarter="Phase 4"
                title="Physical Sovereignty"
                status="upcoming"
                icon={CreditCard}
                delay={0.4}
                items={[
                    "Physical Titanium Ghost Card Forging",
                    "Global Sovereign Concierge launch",
                    "Hardware Enclave Biometric integration",
                    "Sovereign DAO Governance Maturity"
                ]}
            />
        </div>

        <section className="bg-stone-900 text-white p-8 md:p-12 rounded-[40px] border border-white/10 relative overflow-hidden mt-12 shadow-2xl">
            <div className="absolute top-0 right-0 w-96 h-96 bg-nobel-gold opacity-5 blur-[120px] -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative z-10 flex flex-col md:flex-row items-center gap-12">
                <div className="flex-1 space-y-6 text-center md:text-left">
                    <h3 className="text-3xl font-serif font-bold tracking-tight">Future Vision</h3>
                    <p className="text-stone-400 text-base leading-relaxed font-light">
                        Transitioning to full decentralized node operation. The establishment of the Ghost Network as the primary settlement layer for non-traditional commerce globally. Our goal is to enable a circular on-chain economy that operates with zero reliance on legacy banking rails.
                    </p>
                    <div className="flex flex-wrap justify-center md:justify-start gap-4 pt-4">
                         <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-[10px] font-bold uppercase tracking-widest text-stone-400">
                            <Layers size={12} className="text-nobel-gold" /> Decentralized Nodes
                         </div>
                         <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-[10px] font-bold uppercase tracking-widest text-stone-400">
                            <Cpu size={12} className="text-nobel-gold" /> AI Agents
                         </div>
                         <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-[10px] font-bold uppercase tracking-widest text-stone-400">
                            <Globe size={12} className="text-nobel-gold" /> Sovereign Trade
                         </div>
                    </div>
                </div>
                <div className="flex items-center justify-center p-8 bg-white/5 border border-white/10 rounded-[40px] grayscale opacity-50 group hover:grayscale-0 hover:opacity-100 transition-all duration-700">
                    <img src="https://raw.githubusercontent.com/ghost-protocol-labs/ghost-token-sui/refs/heads/main/assets/GHOST.svg" alt="Ghost" className="w-48 h-48 drop-shadow-2xl" />
                </div>
            </div>
        </section>
    </div>
);
